import React, { useEffect, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { connect } from 'react-redux';
import _merge from 'lodash/merge';

import { translator } from 'mage-i18n';
import { FieldsetForObject, HorizontalWrapper, Input, formUtils } from 'moet-core';

import {
  AutoSuggest,
  ContainerTemplate,
  ToggleWrappedContent,
  withReCaptchaProtectedCapability,
} from '@/components';
import { validators } from '@/flow/validation';
import {
  email as emailValidator,
  phoneNumber as phoneNumberValidator,
  registrationNumber as registrationNumberValidator,
} from '@klarna/mf-validations';
import { getRegistrationCourts, getSignupCountries } from '@/selectors/options';
import { getMerchantAwareArrayTranslator } from '@/selectors/merchant';

import {
  BorderlessDropdown,
  ButtonPrimary,
  ButtonSecondary,
  SpacerHorizontal,
  SpacerVertical,
  Typography,
} from '@klarna/bubble-ui';
import { compose } from 'recompose';
import { withResponsive } from 'mage-components';
import { withCapabilityBeforeNext } from '@/flow/common';
import { CAPS } from '@/constants';
import { getDecodedWidgetToken } from '@/utils/widget';

export const VerificationDataCollection = ({
  t,
  tMerchantAware,
  onNext,
  onBack,
  countries,
  registrationCourts,
  error,
}: InferProps<typeof VerificationDataCollection.propTypes>): React.JSX.Element => {
  const tPrefixGeneral = 'products-fe.containers.merchant_verification';
  const tPrefix = 'products-fe.containers.merchant_verification_data_collection';
  const tErrorsPrefix = 'products-fe.error_messages.validation';

  const [fields, setFields] = useState<Record<string, any>>({});
  const [countryField, setCountryField] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [countrySelected, setCountrySelected] = useState('');
  const [toggleValue, setToggleValue] = useState<boolean | undefined>(undefined);
  const [ctaClicked, _setCtaClicked] = useState<boolean | undefined>(undefined);

  const decodedWidgetToken = getDecodedWidgetToken();

  useEffect(() => {
    const formFields = getCorrectFields();
    const countryField = prepareCountryField();

    setFields(formFields);
    setCountryField(countryField);
  }, [countrySelected, toggleValue]);

  const getCorrectFields = () => {
    if (countrySelected === 'DE' && toggleValue === true) {
      return prepareGermanyFields();
    } else if (countrySelected === 'IT' && toggleValue === true) {
      return prepareItalyFields();
    } else if (countrySelected !== 'DE' && countrySelected !== 'IT' && toggleValue === true) {
      return prepareOthersFields();
    } else {
      return prepareNotRegisteredCompanyFields();
    }
  };

  const prepareCountryField = () => {
    const { formFields } = countryField;
    const values = formUtils.getMappedValues(formFields);
    const { country = '' } = values;

    const initCountry = formUtils.initForm({
      country: {
        component: AutoSuggest,
        size: 1,
        label: t(`${tPrefix}.fields.country.label`),
        value: country,
        isInitialized: false,
        isValid: false,
        validators: [
          {
            callback: validators.common.isOneOf,
            errorMessage: t(`${tPrefix}.fields.country.errors.general`),
            options: { subject: countries.map(({ value }) => value) },
          },
        ],
        extraProps: {
          borderless: true,
          options: countries,
          noEmptyOption: true,
          allowCustomValue: false,
        },
      },
    });

    setCountryField({ initCountry });

    return initCountry;
  };

  const prepareGermanyFields = () => {
    const { formFields } = fields;
    const values = formUtils.getMappedValues(formFields);
    const {
      storeEmail = '',
      storePhone = '',
      registrationNumber = '',
      registrationCourt = '',
    } = values;

    const initForm = formUtils.initForm({
      storePhone: {
        component: Input,
        type: 'tel',
        size: 1,
        label: t(`${tPrefix}.fields.phone.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.phone.errors.required`),
          },
          {
            callback: phoneNumberValidator.validate,
            errorMessage: t(`${tPrefix}.fields.phone.errors.invalid`),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: storePhone,
        extraProps: {
          tooltipText: t(`products-fe.containers.contact_details.fields.phone.tooltip`),
        },
      },
      storeEmail: {
        component: Input,
        size: 1,
        type: 'text',
        label: t(`${tPrefix}.fields.email.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.email.errors.required`),
          },
          {
            callback: emailValidator.validate,
            errorMessage: t(`${tPrefix}.fields.email.errors.invalid`),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: storeEmail,
      },
      registrationNumber: {
        component: Input,
        size: 1,
        type: 'text',
        label: t(`${tPrefix}.fields.registration_number.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.registration_number.errors.required`),
          },
          {
            callback: registrationNumberValidator.validate,
            errorMessage: tMerchantAware(`${tErrorsPrefix}.registrationId.general`),
            options: { country: countrySelected },
          },
        ],
        extraProps: {
          tooltipText: t(`${tErrorsPrefix}.registrationId.invalid.DE`),
        },
        isInitialized: false,
        isValid: false,
        value: registrationNumber,
      },
      registrationCourt: {
        component: BorderlessDropdown,
        size: 1.0,
        label: t(`${tPrefix}.fields.registration_court.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.registration_court.errors.required`),
          },
        ],
        extraProps: {
          options: registrationCourts,
        },
        isInitialized: false,
        isValid: false,
        value: registrationCourt,
      },
    });
    setFields({ initForm });

    return initForm;
  };

  const prepareItalyFields = () => {
    const { formFields } = fields;
    const values = formUtils.getMappedValues(formFields);
    const { storeEmail = '', storePhone = '', registrationNumber = '' } = values;

    const initForm = formUtils.initForm({
      storePhone: {
        component: Input,
        type: 'tel',
        size: 1,
        label: t(`${tPrefix}.fields.phone.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.phone.errors.required`),
          },
          {
            callback: phoneNumberValidator.validate,
            errorMessage: t(`${tPrefix}.fields.phone.errors.invalid`),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: storePhone,
        extraProps: {
          tooltipText: t(`products-fe.containers.contact_details.fields.phone.tooltip`),
        },
      },
      storeEmail: {
        component: Input,
        size: 1,
        type: 'text',
        label: t(`${tPrefix}.fields.email.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.email.errors.required`),
          },
          {
            callback: emailValidator.validate,
            errorMessage: t(`${tPrefix}.fields.email.errors.invalid`),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: storeEmail,
      },
      registrationNumber: {
        component: Input,
        size: 1,
        type: 'text',
        label: t(`${tPrefix}.fields.registration_number.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.registration_number.errors.required`),
          },
          {
            callback: registrationNumberValidator.validate,
            errorMessage: tMerchantAware(`${tErrorsPrefix}.registrationId.general`),
            options: { country: countrySelected },
          },
        ],
        extraProps: {
          tooltipText: t(`${tErrorsPrefix}.registrationId.invalid.IT`),
        },
        isInitialized: false,
        isValid: false,
        value: registrationNumber,
      },
    });
    setFields({ initForm });

    return initForm;
  };

  const prepareOthersFields = () => {
    const { formFields } = fields;
    const values = formUtils.getMappedValues(formFields);
    const { storeEmail = '', storePhone = '', registrationNumber = '' } = values;

    const initForm = formUtils.initForm({
      storePhone: {
        component: Input,
        type: 'tel',
        size: 1,
        label: t(`${tPrefix}.fields.phone.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.phone.errors.required`),
          },
          {
            callback: phoneNumberValidator.validate,
            errorMessage: t(`${tPrefix}.fields.phone.errors.invalid`),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: storePhone,
        extraProps: {
          tooltipText: t(`products-fe.containers.contact_details.fields.phone.tooltip`),
        },
      },
      storeEmail: {
        component: Input,
        size: 1,
        type: 'text',
        label: t(`${tPrefix}.fields.email.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.email.errors.required`),
          },
          {
            callback: emailValidator.validate,
            errorMessage: t(`${tPrefix}.fields.email.errors.invalid`),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: storeEmail,
      },
      registrationNumber: {
        component: Input,
        size: 1,
        type: 'text',
        label: t(`${tPrefix}.fields.registration_number.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.registration_number.errors.required`),
          },
          {
            callback: registrationNumberValidator.validate,
            errorMessage: tMerchantAware(`${tErrorsPrefix}.registrationId.general`),
            options: { country: countrySelected },
          },
        ],
        extraProps: {
          autoFocus: true,
          tooltipText: t(`${tErrorsPrefix}.registrationId.invalid`),
        },
        isInitialized: false,
        isValid: false,
        value: registrationNumber,
      },
    });
    setFields({ initForm });

    return initForm;
  };

  const prepareNotRegisteredCompanyFields = () => {
    const { formFields } = fields;
    const values = formUtils.getMappedValues(formFields);
    const {
      storeEmail = '',
      storePhone = '',
      businessOwnerFirstName = '',
      businessOwnerLastName = '',
    } = values;

    const initForm = formUtils.initForm({
      businessOwnerFirstName: {
        component: Input,
        size: 1,
        type: 'text',
        label: t(`${tPrefix}.fields.first_name.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t('products-fe.components.stakeholders.form.first_name.error'),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: businessOwnerFirstName,
      },
      businessOwnerLastName: {
        component: Input,
        size: 1,
        type: 'text',
        label: t(`${tPrefix}.fields.last_name.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t('products-fe.components.stakeholders.form.last_name.error'),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: businessOwnerLastName,
      },
      storePhone: {
        component: Input,
        type: 'tel',
        size: 1,
        label: t(`${tPrefix}.fields.phone.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.phone.errors.required`),
          },
          {
            callback: phoneNumberValidator.validate,
            errorMessage: t(`${tPrefix}.fields.phone.errors.invalid`),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: storePhone,
        extraProps: {
          tooltipText: t(`products-fe.containers.contact_details.fields.phone.tooltip`),
        },
      },
      storeEmail: {
        component: Input,
        size: 1,
        type: 'text',
        label: t(`${tPrefix}.fields.email.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.email.errors.required`),
          },
          {
            callback: emailValidator.validate,
            errorMessage: t(`${tPrefix}.fields.email.errors.invalid`),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: storeEmail,
      },
    });
    setFields({ initForm });

    return initForm;
  };

  const onChange = (key: string, value: string) => {
    const state = {
      fields,
      isLoading,
      isDisabled,
      isEmailFocused,
    };

    formUtils.onChangeFields(key, value, state, fields => setFields(fields));
  };

  const onChangeCountryField = (key: string, value: string) => {
    const state = {
      fields: countryField,
      isLoading,
      isDisabled,
      isEmailFocused,
    };

    formUtils.onChangeFields(key, value, state, countryField => {
      setCountryField(countryField);
    });

    setCountrySelected(value);
  };

  const onFocus = key => {
    if (key === 'storeEmail') {
      setIsEmailFocused(true);
    } else {
      setIsEmailFocused(false);
    }
  };

  const onBlur = key => {
    const state = {
      fields,
      isLoading,
      isDisabled,
      isEmailFocused,
    };
    formUtils.onBlurFields(key, state, fields => setFields(fields));

    if (key === 'storeEmail') {
      setIsEmailFocused(false);
    }
  };

  const onBlurCountryField = key => {
    const state = {
      fields: countryField,
      isLoading,
      isDisabled,
      isEmailFocused,
    };
    formUtils.onBlurFields(key, state, countryFields => setCountryField(countryFields));
  };

  const getFieldValues = () => {
    let values = {};
    Object.entries(fields).forEach(([key, field]) => {
      values = { ...values, [key]: field.value };
    });

    values = { ...values, country: countrySelected };

    return values;
  };

  const onContinue = () => {
    setFields(formUtils.initializeFields(fields));
    const verificationId = decodedWidgetToken?.verificationId;

    const body = getFieldValues();

    formUtils.callToActionOnNextWrapper(fields, () => {
      setIsLoading(true);
      setIsDisabled(true);

      return onNext({ payload: { params: { verificationId }, body } });
    });
  };

  const onToggle = toggleValue => {
    setToggleValue(toggleValue);
  };

  return (
    <ContainerTemplate
      title={t(`${tPrefixGeneral}.title`)}
      subtitle={t(`${tPrefix}.subtitle`)}
      showRecaptchaFooter
      error={error}
    >
      <div style={{ zIndex: 10 }}>
        <FieldsetForObject
          name="country"
          isDisabled={isDisabled}
          fields={countryField}
          onChange={onChangeCountryField}
          onFocus={onFocus}
          onBlur={onBlurCountryField}
        />
      </div>
      <SpacerVertical spaceToken="space/600" />
      <Typography textToken="text-style/headings/blocks/bold/grande">
        {tMerchantAware('products-fe.containers.de_registration_information.title')}
      </Typography>
      <ToggleWrappedContent
        error={ctaClicked && toggleValue === undefined}
        isDisabled={isDisabled}
        toggleOnChange={onToggle}
        toggleValue={toggleValue}
        yesContent={
          <>
            <div style={{ zIndex: 1 }}>
              <FieldsetForObject
                name="registered"
                isDisabled={isDisabled}
                fields={fields}
                onChange={onChange}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            </div>
          </>
        }
        // @ts-expect-error noContent can be null, that's why we get an error. To fix, we need to type the ToggleWrappedContent component
        noContent={
          <>
            <div style={{ zIndex: 1 }}>
              <FieldsetForObject
                name="not-registered"
                isDisabled={isDisabled}
                fields={fields}
                onChange={onChange}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            </div>
          </>
        }
      />
      <SpacerVertical spaceToken="space/400" />
      <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
        <ButtonSecondary onClick={onBack}>{t(`${tPrefixGeneral}.buttons.back`)}</ButtonSecondary>
        <SpacerHorizontal spaceToken="space/200" />
        <ButtonPrimary onClick={onContinue} disabled={isDisabled} loading={isLoading}>
          {t(`${tPrefix}.button_submit`)}
        </ButtonPrimary>
      </HorizontalWrapper>
    </ContainerTemplate>
  );
};

VerificationDataCollection.propTypes = {
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  registrationCourts: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  t: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  error: PropTypes.instanceOf(Error),
};

export default compose(
  connect(state => ({
    t: translator(state),
    tMerchantAware: getMerchantAwareArrayTranslator(state),
    countries: getSignupCountries(state).map(({ key, ...rest }) => ({ value: key, ...rest })),
    registrationCourts: getRegistrationCourts('DE')(state),
  })),
  withCapabilityBeforeNext({
    cap: CAPS.V2_MERCHANTS_MERCHANT_VERIFICATION_POST,
  }),
  withResponsive,
  withReCaptchaProtectedCapability({ setupPost: CAPS.SETUP_POST }),
)(VerificationDataCollection);
